import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { API_ENDPOINTS } from '@core/constants/api-endpoints';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface BackendLoginResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  user: {
    id: number;
    name: string;
    lastName: string;
    username: string;
    phoneNumber: string;
    email: string;
    status: string;
    createdAt: string;
    updatedAt: string;
    roles: Array<{
      id: number;
      name: string;
      description: string;
      permissions: any[];
      permissionCount: number;
      permissionNames: string[];
    }>;
    active: boolean;
    fullName: string;
    roleCount: number;
    allPermissions: string[];
    roleNames: string[];
    totalPermissionCount: number;
  };
  authorities: string[];
  message: string;
  expiresIn: number;
  refreshExpiresIn: number;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  refreshExpiresIn: number;
  message: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: number;
    username: string;
    email: string;
    name: string;
    fullName: string;
    roles: string[];
  };
  expiresIn: number;
  refreshExpiresIn: number;
  authorities: string[];
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface MenuAccessResponse {
  menu: {
    id: string;
    label: string;
    icon: string;
    route?: string;
    children?: MenuAccessResponse['menu'][];
  }[];
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthApiService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const url = `${this.apiUrl}${API_ENDPOINTS.AUTH.LOGIN}`;

      const response$ = this.http
        .post<BackendLoginResponse>(url, credentials)
        .pipe(
          map((backendResponse) => this.transformLoginResponse(backendResponse))
        );

      return await lastValueFrom(response$);
    } catch (error) {
      throw this.handleAuthErrorAsync(error);
    }
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    try {
      const url = `${this.apiUrl}${API_ENDPOINTS.AUTH.REFRESH}`;
      const payload: RefreshTokenRequest = { refreshToken };
      const response$ = this.http.post<RefreshTokenResponse>(url, payload);
      return await lastValueFrom(response$);
    } catch (error) {
      throw this.handleAuthErrorAsync(error);
    }
  }

  private transformLoginResponse(backendResponse: BackendLoginResponse): LoginResponse {
    return {
      accessToken: backendResponse.accessToken,
      refreshToken: backendResponse.refreshToken,
      user: {
        id: backendResponse.user.id,
        username: backendResponse.user.username,
        email: backendResponse.user.email,
        name: backendResponse.user.name,
        fullName: backendResponse.user.fullName,
        roles: backendResponse.user.roleNames,
      },
      expiresIn: backendResponse.expiresIn,
      refreshExpiresIn: backendResponse.refreshExpiresIn,
      authorities: backendResponse.authorities,
    };
  }

  private handleAuthErrorAsync = (error: any): never => {
    const errorMessages: Record<number, string> = {
      401: 'Credenciales inválidas',
      403: 'No tienes permisos para acceder',
      404: 'Servicio no encontrado',
      422: error.error?.message || 'Datos inválidos',
      429: 'Demasiados intentos. Intenta más tarde',
      500: 'Error del servidor. Intenta más tarde',
    };

    const errorMessage = errorMessages[error.status] || 'Error de conexión';
    throw new Error(errorMessage);
  };
}
